// src/pages/trading-portal/TradeExecution/AIAgentPage.js
// AI Trading Agent Management - 4-tab interface: Agent Management, Prompt Editor, Decision Log, Performance

import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    Card,
    CardContent,
    FormControlLabel,
    FormGroup,
    Checkbox,
    Switch,
    Slider,
    Collapse,
    Menu,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { createChart, ColorType, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import ConnectAccountTab from './ConnectAccountTab';
import {
    AIAgentStatus, AIAgentStatusLabels, AIAgentStatusColors,
    AIAgentDecisionAction, AIAgentDecisionActionLabels, AIAgentDecisionActionColors,
    CycleStatus, CycleStatusLabels, CycleStatusColors,
    PromptVersionStatus, PromptVersionStatusLabels, PromptVersionStatusColors,
    TimeFrame, TimeFrameLabels,
} from '../stockTradingEnums';
import { openLiveMarketDataStream, timeframeToSeconds } from '../../../services/streamingMarketData';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// Timeframe ↔ seconds mapping. Used by `secondsToTimeframe` as a fallback when
// editing legacy agents that pre-date the Decision Timing split (no
// `config_json.timeframe` set). New agents store `timeframe` and
// `cycle_interval_seconds` independently; the scheduler / Live Market Data
// auto-refresh use seconds (Decision Timing) and `_gather_market_data` uses
// the explicit timeframe for candle/VWAP/ATR calculations.
const TIMEFRAME_SECONDS = {
    ONE_MIN: 60,
    TWO_MIN: 120,
    THREE_MIN: 180,
    FOUR_MIN: 240,
    FIVE_MIN: 300,
    FIFTEEN_MIN: 900,
    ONE_HOUR: 3600,
    TWO_HOUR: 7200,
    FOUR_HOUR: 14400,
    FIVE_HOUR: 18000,
    DAILY: 86400,
    WEEKLY: 604800,
    MONTHLY: 2592000,
};
const secondsToTimeframe = (secs) => {
    const s = Number(secs);
    const match = Object.entries(TIMEFRAME_SECONDS).find(([, v]) => v === s);
    return match ? match[0] : 'FIVE_MIN';
};

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const bodyCellSx = { fontFamily, fontSize: '0.85rem', color: '#334155' };

// Default Martingale ladder used by the LLM data snapshot's sizing_t1..t4_*
// fields. Mirrors backend DEFAULT_POSITION_SIZING in AIAgentController.py — keep
// the two in sync if the platform-wide default ever changes.
const DEFAULT_POSITION_SIZING = { divisor: 15, multipliers: [1, 2, 4, 8] };

const INITIAL_AGENT_FORM = {
    name: '',
    description: '',
    execution_mode: 'NO_LLM',
    symbol: '',
    symbol_id: '',
    broker_connection_id: '',
    temperature: 0.3,
    max_output_tokens: 12000,
    timeframe: 'FIVE_MIN',
    cycle_interval_seconds: 30,
    max_daily_cycles: 50,
    auto_execute: false,
    capital_allocation: '',
    max_position_value: '',
    max_daily_loss: '',
    max_open_positions: 3,
    trading_start_time: '',
    trading_end_time: '',
    trading_timezone: 'America/Chicago',
    enabled_agents: {
        macro_strategist: true,
        options_volatility: true,
        asset_strategist: true,
    },
};

// ==================== PROMPT BUILDER VARIABLE CATALOG ====================
// Mirrors backend DEFAULT_DATA_TEMPLATE (backend/controller/StockTrading/AIAgentDefaultPrompt.py).
// Each variable can be dragged/clicked into the System Prompt (inserts as
// `name` — backticked reference) or the Data Template (inserts as {name} — the
// str.format placeholder the backend substitutes at cycle time).
//
// Variable `agent` tag: when set, the variable is hidden from the palette if
// that strategist agent is disabled on the selected agent
// (config_json.enabled_agents). Untagged variables are supporting/core fields
// and are always shown (VWAP execution, portfolio, snapshot, etc.).
const PROMPT_VARIABLES = [
    { category: 'Snapshot', color: '#64748b', vars: [
        { name: 'timestamp', label: 'Timestamp' },
        { name: 'symbol', label: 'Symbol' },
    ]},
    { category: 'Broader Market', color: '#0ea5e9', vars: [
        { name: 'spy_daily_pct', label: 'SPY Daily Change %' },
        { name: 'avg_daily_volume', label: 'Avg Daily Volume (20d)' },
        { name: 'earnings_today', label: 'Earnings Today' },
        { name: 'fear_greed_index', label: 'Fear & Greed Index' },
    ]},
    { category: 'Technical Data', color: '#0ea5e9', vars: [
        { name: 'current_price', label: 'Current Price' },
        { name: 'vwap_upper', label: 'VWAP Upper' },
        { name: 'vwap_mid', label: 'VWAP Mid' },
        { name: 'vwap_lower', label: 'VWAP Lower' },
        { name: 'current_atr', label: 'ATR (14)' },
    ]},
    { category: 'Order Flow / L2', color: '#0ea5e9', vars: [
        { name: 'cmf', label: 'CMF (Chaikin Money Flow)' },
        { name: 'bbw', label: 'BBW (Bollinger Band Width)' },
        { name: 'bbw_14d_min', label: 'BBW 14d Min' },
        { name: 'bbw_14d_max', label: 'BBW 14d Max' },
        { name: 'bbw_is_tightest_14d', label: 'BBW Is 14d Tightest' },
        { name: 'l2_imbalance', label: 'L2 Imbalance (Ask/Bid)' },
    ]},
    { category: 'Options Data', color: '#8b5cf6', vars: [
        { name: 'put_wall', label: 'Put Wall', agent: 'options_volatility' },
        { name: 'call_wall', label: 'Call Wall', agent: 'options_volatility' },
        { name: 'gamma_flip', label: 'Gamma Flip', agent: 'options_volatility' },
        { name: 'max_pain', label: 'Max Pain', agent: 'options_volatility' },
        { name: 'dte', label: 'DTE (Nearest Expiry)', agent: 'options_volatility' },
    ]},
    { category: 'Macro Liquidity', color: '#059669', vars: [
        { name: 'fed_balance_sheet', label: 'Fed Balance Sheet', agent: 'macro_strategist' },
        { name: 'tga', label: 'TGA', agent: 'macro_strategist' },
        { name: 'reverse_repo', label: 'Reverse Repo', agent: 'macro_strategist' },
        { name: 'net_liquidity', label: 'Net Liquidity', agent: 'macro_strategist' },
        { name: 'wow_liquidity_change', label: 'WoW Liquidity Change', agent: 'macro_strategist' },
        { name: 'macro_bias', label: 'Macro Bias', agent: 'macro_strategist' },
        { name: 'prev_fed_balance_sheet', label: 'Prev Fed Balance Sheet', agent: 'macro_strategist' },
        { name: 'prev_tga', label: 'Prev TGA', agent: 'macro_strategist' },
        { name: 'prev_reverse_repo', label: 'Prev Reverse Repo', agent: 'macro_strategist' },
        { name: 'prev_net_liquidity', label: 'Prev Net Liquidity', agent: 'macro_strategist' },
        { name: 'market_trend_state', label: 'Market Trend State', agent: 'macro_strategist' },
    ]},
    { category: 'Crypto Arbitrage', color: '#f59e0b', vars: [
        { name: 'kimchi_premium', label: 'Kimchi Premium', agent: 'asset_strategist' },
        { name: 'cbp', label: 'Coinbase Premium (CBP)', agent: 'asset_strategist' },
        { name: 'krw_spread', label: 'KRW Spread', agent: 'asset_strategist' },
        { name: 'mnav', label: 'mNAV', agent: 'asset_strategist' },
        { name: 'btc_correlation', label: 'BTC Correlation (60m)', agent: 'asset_strategist' },
    ]},
    { category: 'Portfolio State', color: '#ec4899', vars: [
        { name: 'total_capital', label: 'Total Capital' },
        { name: 'cycle_trades', label: 'Cycle Trades' },
        { name: 'equity_avg_cost', label: 'Equity Avg Cost' },
        { name: 'strangle_status', label: 'Strangle Status', agent: 'options_volatility' },
        { name: 'open_positions', label: 'Open Positions' },
    ]},
    { category: 'Strategic Flags', color: '#dc2626', vars: [
        { name: 'is_crypto_proxy', label: 'Is Crypto Proxy', agent: 'asset_strategist' },
        { name: 'has_mnav', label: 'Has mNAV', agent: 'asset_strategist' },
        { name: 'is_24_7_asset', label: 'Trades 24/7', agent: 'asset_strategist' },
        { name: 'gamma_regime', label: 'Gamma Regime', agent: 'options_volatility' },
        { name: 'out_of_bounds', label: 'Out of Bounds', agent: 'options_volatility' },
        { name: 'math_put', label: 'Math Put', agent: 'options_volatility' },
        { name: 'math_call', label: 'Math Call', agent: 'options_volatility' },
        { name: 'selected_put_strike', label: 'Selected Put Strike', agent: 'options_volatility' },
        { name: 'selected_call_strike', label: 'Selected Call Strike', agent: 'options_volatility' },
        { name: 'active_put_strike', label: 'Active Put Strike', agent: 'options_volatility' },
        { name: 'active_call_strike', label: 'Active Call Strike', agent: 'options_volatility' },
        { name: 'volarb_action', label: 'Vol-Arb Action', agent: 'options_volatility' },
        { name: 'system_halt', label: 'System Halt' },
        { name: 'market_state', label: 'Market State' },
        { name: 'authorized_direction', label: 'Authorized Direction' },
        { name: 'max_trades_allowed', label: 'Max Trades Allowed' },
        { name: 'ibit_rs_state', label: 'IBIT RS State', agent: 'asset_strategist' },
    ]},
    { category: 'Execution Gates', color: '#6366f1', vars: [
        { name: 'is_in_trading_hours', label: 'In Trading Hours' },
        { name: 'last_entry_price', label: 'Last Entry Price' },
        { name: 'candles_since_last_trade', label: 'Candles Since Last Trade' },
        { name: 'red_candles_since_last_trade', label: 'Red Candles Since Last Trade' },
        { name: 'green_candles_since_last_trade', label: 'Green Candles Since Last Trade' },
        { name: 'scaling_ready_long', label: 'Scaling Ready LONG' },
        { name: 'scaling_ready_short', label: 'Scaling Ready SHORT' },
        { name: 'last_closed_candle_color', label: 'Last Closed Candle Color' },
        { name: 'candle_body_ratio', label: 'Candle Body Ratio' },
        { name: 'candle_wick_long_ratio', label: 'Candle Wick Long Ratio' },
        { name: 'candle_wick_short_ratio', label: 'Candle Wick Short Ratio' },
    ]},
    { category: 'Position Sizing (1-2-4-8)', color: '#14b8a6', vars: [
        { name: 'sizing_unit', label: 'Unit Size (Capital / 15)' },
        { name: 'sizing_t1_dollars', label: 'Trade 1 $ (1x)' },
        { name: 'sizing_t1_shares', label: 'Trade 1 Shares (1x)' },
        { name: 'sizing_t2_dollars', label: 'Trade 2 $ (2x)' },
        { name: 'sizing_t2_shares', label: 'Trade 2 Shares (2x)' },
        { name: 'sizing_t3_dollars', label: 'Trade 3 $ (4x)' },
        { name: 'sizing_t3_shares', label: 'Trade 3 Shares (4x)' },
        { name: 'sizing_t4_dollars', label: 'Trade 4 $ (8x)' },
        { name: 'sizing_t4_shares', label: 'Trade 4 Shares (8x)' },
    ]},
    { category: 'History & Recent Candles', color: '#64748b', vars: [
        { name: 'recent_candles', label: 'Recent Candles (last 1 hour)' },
        { name: 'realized_pnl', label: 'Realized P&L' },
        { name: 'unrealized_pnl', label: 'Unrealized P&L' },
        { name: 'total_pnl', label: 'Total P&L' },
        { name: 'win_rate', label: 'Win Rate' },
        { name: 'wins_losses', label: 'Wins / Losses' },
        { name: 'current_streak', label: 'Current Streak' },
        { name: 'recent_fills', label: 'Recent Fills (last 10)' },
        { name: 'recent_decisions', label: 'Recent Decisions (last 5)' },
    ]},
];

// ==================== STAT CARD ====================

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color, fontSize: 24 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>{title}</Typography>
                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{value}</Typography>
                    {subtitle && <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{subtitle}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// lightweight-charts accepts either "yyyy-mm-dd" or a UNIX second timestamp.
// Backend returns "yyyy-mm-dd HH:MM" for intraday bars, ALREADY shifted to the
// agent's timezone by `_ts_in_agent_tz` server-side. Parse as UTC (append 'Z')
// so lightweight-charts' UTC formatter renders the same wall-clock value.
const toChartTime = (t) => {
    if (t == null) return null;
    if (typeof t === 'number') return t;
    if (typeof t !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    let parsed = t.includes('T') ? t : t.replace(' ', 'T');
    if (!/Z$|[+-]\d{2}:?\d{2}$/.test(parsed)) parsed += 'Z';
    const ms = Date.parse(parsed);
    return isNaN(ms) ? null : Math.floor(ms / 1000);
};

// CandleChart MUST live at module scope. If it were defined inside AIAgentPage,
// every parent re-render (e.g. each SSE tick calling setMarketData) would
// produce a new forwardRef object — React would treat it as a different
// component type and unmount/remount the chart, throwing away every live bar
// that updateBar() just pushed in. That symptom is "Live Market Data tab is
// frozen even though SSE is connected and emitting ticks."
const CandleChart = forwardRef(({ data, height = 260, secondsVisible = false }, ref) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return undefined;

        const chart = createChart(containerRef.current, {
            width: containerRef.current.clientWidth,
            height,
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: '#94a3b8',
                fontSize: 11,
            },
            grid: {
                vertLines: { color: '#f1f5f9' },
                horzLines: { color: '#f1f5f9' },
            },
            rightPriceScale: { borderColor: '#e2e8f0' },
            timeScale: { borderColor: '#e2e8f0', timeVisible: true, secondsVisible },
            crosshair: { mode: 0 },
        });
        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#22c55e', downColor: '#ef4444',
            borderUpColor: '#22c55e', borderDownColor: '#ef4444',
            wickUpColor: '#22c55e', wickDownColor: '#ef4444',
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });
        candleSeriesRef.current = candleSeries;

        const volumeSeries = chart.addSeries(HistogramSeries, {
            priceFormat: { type: 'volume' },
            priceScaleId: 'volume',
        });
        volumeSeriesRef.current = volumeSeries;
        chart.priceScale('volume').applyOptions({
            scaleMargins: { top: 0.85, bottom: 0 },
        });

        const handleResize = () => {
            if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
            chartRef.current = null;
            candleSeriesRef.current = null;
            volumeSeriesRef.current = null;
        };
    }, [height, secondsVisible]);

    useEffect(() => {
        if (!candleSeriesRef.current || !volumeSeriesRef.current) return;
        if (!data || data.length === 0) {
            candleSeriesRef.current.setData([]);
            volumeSeriesRef.current.setData([]);
            return;
        }
        const candleData = data
            .filter(d => d.time && d.open != null && d.close != null)
            .map(d => ({ time: toChartTime(d.time), open: d.open, high: d.high, low: d.low, close: d.close }))
            .filter(d => d.time != null)
            .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        const volumeData = data
            .filter(d => d.time && d.volume != null)
            .map(d => ({
                time: toChartTime(d.time), value: d.volume,
                color: (d.close || 0) >= (d.open || 0) ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)',
            }))
            .filter(d => d.time != null)
            .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        candleSeriesRef.current.setData(candleData);
        volumeSeriesRef.current.setData(volumeData);
        chartRef.current?.timeScale().fitContent();
    }, [data]);

    useImperativeHandle(ref, () => ({
        updateBar(bar) {
            if (!bar || !candleSeriesRef.current) return;
            const t = typeof bar.time === 'number' ? bar.time : toChartTime(bar.time);
            if (t == null) return;
            try {
                candleSeriesRef.current.update({
                    time: t,
                    open: bar.open, high: bar.high, low: bar.low, close: bar.close,
                });
                if (volumeSeriesRef.current && bar.volume != null) {
                    volumeSeriesRef.current.update({
                        time: t,
                        value: bar.volume,
                        color: (bar.close ?? 0) >= (bar.open ?? 0)
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(239,68,68,0.3)',
                    });
                }
            } catch (e) {
                // Lightweight-Charts throws if the new bar's time is older
                // than the most recent point. Swallow — usually means a
                // late-arriving tick after a bucket close, safe to drop.
            }
        },
    }), []);

    return <Box ref={containerRef} sx={{ width: '100%', minHeight: height }} />;
});

// ==================== MAIN COMPONENT ====================

const AIAgentPage = () => {
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    // Watchlist Symbols
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);
    const [symbolsLoading, setSymbolsLoading] = useState(false);

    // Broker connections (for agent -> Alpaca linkage)
    const [brokerConnections, setBrokerConnections] = useState([]);
    const [brokerConnectionsLoading, setBrokerConnectionsLoading] = useState(false);

    // Timezones (for trading-window configuration)
    const [timezones, setTimezones] = useState([]);

    // Tab 0: Agent Management
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const [agentsPage, setAgentsPage] = useState(0);
    const [agentsPerPage, setAgentsPerPage] = useState(10);
    const [agentsTotal, setAgentsTotal] = useState(0);
    const [agentDialog, setAgentDialog] = useState(false);
    const [agentEditing, setAgentEditing] = useState(null);
    const [agentForm, setAgentForm] = useState({ ...INITIAL_AGENT_FORM });
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Tab 1: Prompt Editor
    const [prompts, setPrompts] = useState([]);
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [promptEditorValue, setPromptEditorValue] = useState('');
    const [dataTemplateValue, setDataTemplateValue] = useState('');
    const [promptName, setPromptName] = useState('');
    const [promptNotes, setPromptNotes] = useState('');
    const [showDataTemplate, setShowDataTemplate] = useState(false);

    // Tab 1: Position Sizing (Martingale ladder editor — collapsible inside Prompt Editor).
    // Sourced from `selectedAgent.config_json.position_sizing` and persisted via
    // PUT /api/trade-execution/ai-agents/:id with `position_sizing: {...}`.
    const [showSizingEditor, setShowSizingEditor] = useState(false);
    const [sizingDivisor, setSizingDivisor] = useState(DEFAULT_POSITION_SIZING.divisor);
    const [sizingMultipliers, setSizingMultipliers] = useState([...DEFAULT_POSITION_SIZING.multipliers]);
    const [sizingSubmitting, setSizingSubmitting] = useState(false);
    const [sizingDirty, setSizingDirty] = useState(false);

    // Tab 1: Prompt Builder (variables palette + copy-from-preset)
    const [focusedField, setFocusedField] = useState(null); // 'system_prompt' | 'data_template' | null
    const systemPromptRef = useRef(null);
    const dataTemplateRef = useRef(null);
    const [variableSearch, setVariableSearch] = useState('');
    const [showVariables, setShowVariables] = useState(true);
    const [presetTemplates, setPresetTemplates] = useState([]);
    const [presetsLoaded, setPresetsLoaded] = useState(false);
    const [presetMenuAnchor, setPresetMenuAnchor] = useState(null);

    // Tab 2: Decision Log
    const [decisions, setDecisions] = useState([]);
    const [decisionsLoading, setDecisionsLoading] = useState(false);
    const [decisionsPage, setDecisionsPage] = useState(0);
    const [decisionsPerPage, setDecisionsPerPage] = useState(20);
    const [decisionsTotal, setDecisionsTotal] = useState(0);
    const [expandedDecision, setExpandedDecision] = useState(null);
    const [decisionsSourceFilter, setDecisionsSourceFilter] = useState('ALL');  // ALL | LLM | PURE_CODE_ROUTER

    // Tab 3: Performance
    const [performance, setPerformance] = useState(null);
    const [performanceLoading, setPerformanceLoading] = useState(false);
    const [cycles, setCycles] = useState([]);
    const [cyclesLoading, setCyclesLoading] = useState(false);

    // Tab 4: Live Market Data
    const [marketData, setMarketData] = useState(null);
    const [marketDataLoading, setMarketDataLoading] = useState(false);
    const [streamLive, setStreamLive] = useState(false);
    // Imperative refs into the two CandleChart instances on the Live Market
    // Data tab. The SSE handler below calls .updateBar() on these without
    // triggering a React re-render, which is what makes the chart move
    // smoothly per-tick (TradingView style) instead of flickering on each
    // setData rebuild.
    const chartTopRef = useRef(null);     // user-TF chart (Recent Candles)
    const chartBottomRef = useRef(null);  // 30s-tail chart (Last 5 Minutes)
    // In-progress bar state per chart, keyed off the current bucket time.
    // Reset to null on agent change / SSE reopen so a stale bucket from a
    // previous symbol can't leak into the new chart.
    const topInProgressRef = useRef(null);
    const bottomInProgressRef = useRef(null);
    const [fetchingCrypto, setFetchingCrypto] = useState(false);
    const [fetchingL2, setFetchingL2] = useState(false);
    const [fetchingMacro, setFetchingMacro] = useState(false);

    // Tab 5: P&L Report
    const [pnlData, setPnlData] = useState(null);
    const [pnlLoading, setPnlLoading] = useState(false);
    const [pnlPage, setPnlPage] = useState(0);
    const [pnlPerPage, setPnlPerPage] = useState(20);
    const [pnlTotal, setPnlTotal] = useState(0);

    // ==================== PERMISSIONS ====================

    useEffect(() => {
        const checkPerms = async () => {
            setCanView(await hasPermission('Trade Execution Engine', 'can_view'));
            setCanAdd(await hasPermission('Trade Execution Engine', 'can_add'));
            setCanEdit(await hasPermission('Trade Execution Engine', 'can_edit'));
            setCanDelete(await hasPermission('Trade Execution Engine', 'can_delete'));
        };
        checkPerms();
    }, []);

    // ==================== DATA LOADING ====================

    const fetchWatchlistSymbols = useCallback(async () => {
        setSymbolsLoading(true);
        try {
            const params = new URLSearchParams({ page: 1, per_page: 500, status: 'ACTIVE' });
            const response = await axiosInstance.get(`/api/stock-trading/symbols?${params.toString()}`);
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        } finally {
            setSymbolsLoading(false);
        }
    }, []);

    const fetchBrokerConnections = useCallback(async () => {
        setBrokerConnectionsLoading(true);
        try {
            const res = await axiosInstance.get('/api/trade-execution/broker-connections');
            setBrokerConnections(res.data?.data || res.data || []);
        } catch (err) {
            console.error('Failed to fetch broker connections:', err);
        } finally {
            setBrokerConnectionsLoading(false);
        }
    }, []);

    const fetchTimezones = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/selections/timezones');
            const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setTimezones(list);
        } catch (err) {
            console.error('Failed to fetch timezones:', err);
        }
    }, []);

    const loadAgents = useCallback(async () => {
        setAgentsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents?page=${agentsPage + 1}&limit=${agentsPerPage}`);
            setAgents(res.data.data || []);
            setAgentsTotal(res.data.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load agents');
        } finally {
            setAgentsLoading(false);
        }
    }, [agentsPage, agentsPerPage]);

    const loadPrompts = useCallback(async (agentId) => {
        if (!agentId) return;
        setPromptsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/prompts`);
            setPrompts(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load prompts');
        } finally {
            setPromptsLoading(false);
        }
    }, []);

    const loadDecisions = useCallback(async (agentId) => {
        if (!agentId) return;
        setDecisionsLoading(true);
        try {
            const sourceParam = decisionsSourceFilter && decisionsSourceFilter !== 'ALL'
                ? `&source=${decisionsSourceFilter}` : '';
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${agentId}/decisions?page=${decisionsPage + 1}&limit=${decisionsPerPage}${sourceParam}`
            );
            setDecisions(res.data.data || []);
            setDecisionsTotal(res.data.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load decisions');
        } finally {
            setDecisionsLoading(false);
        }
    }, [decisionsPage, decisionsPerPage, decisionsSourceFilter]);

    const loadPerformance = useCallback(async (agentId) => {
        if (!agentId) return;
        setPerformanceLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/performance`);
            setPerformance(res.data.data || null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load performance');
        } finally {
            setPerformanceLoading(false);
        }
    }, []);

    const loadCycles = useCallback(async (agentId) => {
        if (!agentId) return;
        setCyclesLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/cycles?page=1&limit=10`);
            setCycles(res.data.data || []);
        } catch (err) {
            console.error('Failed to load cycles');
        } finally {
            setCyclesLoading(false);
        }
    }, []);

    const loadMarketData = useCallback(async (agentId) => {
        if (!agentId) return;
        setMarketDataLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/market-data`);
            setMarketData(res.data.data || null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load market data');
        } finally {
            setMarketDataLoading(false);
        }
    }, []);

    const refreshMarketData = useCallback(async (agentId, silent = false) => {
        if (!agentId) return;
        if (!silent) setMarketDataLoading(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${agentId}/market-data/refresh`);
            setMarketData(res.data.data || null);
            if (!silent) setSnackbar({ message: 'Market data refreshed', severity: 'success' });
        } catch (err) {
            if (!silent) setError(err.response?.data?.error || 'Failed to refresh market data');
        } finally {
            if (!silent) setMarketDataLoading(false);
        }
    }, []);

    const loadPnlReport = useCallback(async (agentId, page = 1, perPage = 20) => {
        if (!agentId) return;
        setPnlLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/pnl-report?page=${page}&limit=${perPage}`);
            setPnlData(res.data.data || null);
            setPnlTotal(res.data.data?.pagination?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load P&L report');
        } finally {
            setPnlLoading(false);
        }
    }, []);

    useEffect(() => { loadAgents(); fetchWatchlistSymbols(); fetchBrokerConnections(); fetchTimezones(); }, [loadAgents, fetchWatchlistSymbols, fetchBrokerConnections, fetchTimezones]);

    useEffect(() => {
        if (selectedAgent) {
            if (activeTab === 2) { loadPrompts(selectedAgent.id); loadMarketData(selectedAgent.id); }
            if (activeTab === 3) loadDecisions(selectedAgent.id);
            if (activeTab === 4) { loadPerformance(selectedAgent.id); loadCycles(selectedAgent.id); }
            if (activeTab === 5) refreshMarketData(selectedAgent.id);
            if (activeTab === 6) loadPnlReport(selectedAgent.id, pnlPage + 1, pnlPerPage);
        }
    }, [selectedAgent, activeTab, loadPrompts, loadDecisions, loadPerformance, loadCycles, refreshMarketData, loadMarketData, loadPnlReport, pnlPage, pnlPerPage]);

    // Prompt Editor → Position Sizing: refresh Current Price at the agent's
    // Decision Timing cadence so the share counts stay aligned with the live
    // market price the next cycle will see.
    useEffect(() => {
        if (activeTab !== 2 || !selectedAgent?.id) return undefined;
        const intervalSec = Math.max(5, selectedAgent?.cycle_interval_seconds || 30);
        const id = setInterval(() => {
            loadMarketData(selectedAgent.id);
        }, intervalSec * 1000);
        return () => clearInterval(id);
    }, [activeTab, selectedAgent?.id, selectedAgent?.cycle_interval_seconds, loadMarketData]);

    // Hydrate the position-sizing editor whenever the selected agent changes.
    // Falls back to the platform default when the agent has no override stored.
    useEffect(() => {
        const ps = selectedAgent?.config_json?.position_sizing;
        if (ps && typeof ps === 'object' && Array.isArray(ps.multipliers) && ps.multipliers.length === 4) {
            setSizingDivisor(Number(ps.divisor) || DEFAULT_POSITION_SIZING.divisor);
            setSizingMultipliers(ps.multipliers.map(m => Number(m) || 0));
        } else {
            setSizingDivisor(DEFAULT_POSITION_SIZING.divisor);
            setSizingMultipliers([...DEFAULT_POSITION_SIZING.multipliers]);
        }
        setSizingDirty(false);
    }, [selectedAgent?.id, selectedAgent?.config_json]);

    // Live streaming via SSE — replaces the previous setInterval polling.
    //
    // Flow on tab open:
    //   1. The activeTab===5 effect above already kicked a one-shot
    //      refreshMarketData() call to populate the initial state + chart bars.
    //   2. This effect opens an SSE connection to the backend
    //      (/api/trade-execution/ai-agents/:id/market-data/stream) which
    //      forwards live trades from Alpaca's WebSocket SIP feed. Every trade
    //      arrives as a `tick` event; closed 30s buckets arrive as `bar_30s`.
    //   3. On each tick: setMarketData() updates the price + VWAP bands
    //      (cheap React reconciliation, no chart remount), AND we compute
    //      bucket times for both charts and push the in-progress candle into
    //      lightweight-charts via the imperative .updateBar() method. The
    //      TradingView chart merges by time — same time = update last candle
    //      in place; new time = append. End result: smooth, TradingView-like
    //      live updates with no page/section refresh.
    //
    // Timezone alignment: backend emits UTC timestamps in `ts`. The initial
    // REST-loaded bars are TZ-shifted server-side to the agent's TZ
    // (recent_candles_data[].time = "yyyy-mm-dd HH:MM" in agent TZ). To keep
    // the live in-progress bar visually aligned with the existing bars on the
    // chart, we apply the same shift here in the browser using the
    // marketData.agent_timezone string.
    const utcSecondsToAgentTzShifted = useCallback((utcMillis, tzName) => {
        if (!tzName) return Math.floor(utcMillis / 1000);
        try {
            const fmt = new Intl.DateTimeFormat('en-US', {
                timeZone: tzName,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false,
            });
            const parts = fmt.formatToParts(new Date(utcMillis));
            const get = (type) => parts.find(p => p.type === type)?.value || '00';
            const y = get('year'); const m = get('month'); const day = get('day');
            const h = get('hour') === '24' ? '00' : get('hour');
            const mn = get('minute'); const s = get('second');
            const ms = Date.parse(`${y}-${m}-${day}T${h}:${mn}:${s}Z`);
            return Number.isNaN(ms) ? Math.floor(utcMillis / 1000) : Math.floor(ms / 1000);
        } catch (e) {
            return Math.floor(utcMillis / 1000);
        }
    }, []);

    // Hold tzName in a ref so the SSE handler closure always reads the latest
    // value without forcing the useEffect below to tear down the stream every
    // time agent_timezone changes (which happens when the REST snapshot lands
    // a few hundred ms after the SSE connects).
    const tzNameRef = useRef(null);
    useEffect(() => {
        tzNameRef.current = marketData?.agent_timezone || selectedAgent?.trading_timezone || null;
    }, [marketData?.agent_timezone, selectedAgent?.trading_timezone]);

    useEffect(() => {
        if (activeTab !== 5 || !selectedAgent?.id) return undefined;

        // Reset per-chart in-progress bar state so a previous agent's bucket
        // can't leak into the new agent's chart on tab/agent switch.
        topInProgressRef.current = null;
        bottomInProgressRef.current = null;
        setStreamLive(false);

        // Streaming-disabled fallback: when ALPACA_STREAM_ENABLED=false on the
        // backend the Alpaca WebSocket pipeline is off, so no `tick` events
        // will ever flow through SSE. Instead, poll the lightweight
        // /market-data endpoint at the agent's Decision Timing cadence —
        // that's the user-defined refresh rate for the stream-derived fields
        // (current_price, VWAP bands, intra-30s bars). Each poll replaces
        // marketData wholesale so the chart's [data] effect fires and the
        // displayed price/VWAP/candles refresh together.
        //
        // Why `=== false` rather than truthy-check: on the very first render
        // marketData is still null (REST hasn't landed yet) and we want to
        // open SSE optimistically so streaming-on users get instant ticks.
        // Once the REST response lands with the actual flag, this effect
        // re-runs and either keeps the SSE (true / undefined) or tears it
        // down and starts polling (false).
        if (marketData?.streaming_enabled === false) {
            const intervalSec = Math.max(
                5,
                selectedAgent?.cycle_interval_seconds || 300,
            );
            const id = setInterval(() => {
                loadMarketData(selectedAgent.id);
            }, intervalSec * 1000);
            return () => clearInterval(id);
        }

        const userTfSec = timeframeToSeconds(
            selectedAgent?.config_json?.timeframe || 'FIVE_MIN'
        );

        const updateInProgressBar = (stateRef, chartRef, tickSec, bucketSec, price) => {
            const bucketStart = Math.floor(tickSec / bucketSec) * bucketSec;
            let bar = stateRef.current;
            if (!bar || bar.time !== bucketStart) {
                bar = { time: bucketStart, open: price, high: price, low: price, close: price, volume: 0 };
            } else {
                if (price > bar.high) bar.high = price;
                if (price < bar.low) bar.low = price;
                bar.close = price;
            }
            stateRef.current = bar;
            chartRef.current?.updateBar(bar);
        };

        const stream = openLiveMarketDataStream(selectedAgent.id, {
            onSnapshot: (snap) => {
                setStreamLive(true);
                // Seed marketData if REST hasn't returned yet so the first
                // visible price comes from the snapshot — otherwise the
                // initial frame is silently dropped.
                setMarketData(prev => ({
                    ...(prev || {}),
                    current_price: snap.price || prev?.current_price,
                    vwap_mid: snap.vwap_mid || prev?.vwap_mid,
                    vwap_upper: snap.vwap_upper || prev?.vwap_upper,
                    vwap_lower: snap.vwap_lower || prev?.vwap_lower,
                }));
            },
            onTick: (evt) => {
                setStreamLive(true);
                setMarketData(prev => ({
                    ...(prev || {}),
                    current_price: evt.price,
                    vwap_mid: evt.vwap_mid,
                    vwap_upper: evt.vwap_upper,
                    vwap_lower: evt.vwap_lower,
                }));

                if (!evt.ts || evt.price == null) return;
                const tickMs = Date.parse(evt.ts);
                if (Number.isNaN(tickMs)) return;
                const tickSec = utcSecondsToAgentTzShifted(tickMs, tzNameRef.current);

                updateInProgressBar(topInProgressRef, chartTopRef, tickSec, userTfSec, evt.price);
                updateInProgressBar(bottomInProgressRef, chartBottomRef, tickSec, 30, evt.price);
            },
            onBar30s: (bar) => {
                if (!bar?.time) return;
                // bar.time from backend is naive UTC isoformat (the bucket
                // start). Convert to agent-TZ-shifted unix seconds the same
                // way ticks are.
                const ms = Date.parse(bar.time.endsWith('Z') ? bar.time : bar.time + 'Z');
                if (Number.isNaN(ms)) return;
                const t = utcSecondsToAgentTzShifted(ms, tzNameRef.current);
                chartBottomRef.current?.updateBar({
                    time: t,
                    open: bar.open, high: bar.high, low: bar.low, close: bar.close,
                    volume: bar.volume,
                });
                // Force the next tick to start a fresh in-progress 30s bucket
                // off this confirmed close.
                bottomInProgressRef.current = null;
            },
            onError: (err) => {
                // Network blip / token expired / server restart — the polyfill
                // auto-reconnects, so we just flip the indicator to "stale"
                // until the next event lands.
                console.warn('Live Market Data stream error:', err);
                setStreamLive(false);
            },
        });

        return () => {
            stream.close();
            setStreamLive(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeTab,
        selectedAgent?.id,
        selectedAgent?.config_json?.timeframe,
        selectedAgent?.cycle_interval_seconds,
        marketData?.streaming_enabled,
        loadMarketData,
        utcSecondsToAgentTzShifted,
    ]);

    useEffect(() => {
        if (!selectedAgent && activeTab >= 2) setActiveTab(0);
        // Prompt Editor (tab 2) is hidden for NO_LLM agents — bounce to Decision Log.
        if (selectedAgent && selectedAgent.execution_mode === 'NO_LLM' && activeTab === 2) {
            setActiveTab(3);
        }
    }, [selectedAgent, activeTab]);

    // ==================== AGENT CRUD HANDLERS ====================

    const handleCreateAgent = async () => {
        setSubmitting(true);
        try {
            const { enabled_agents, ...rest } = agentForm;
            const payload = {
                ...rest,
                enabled_agents,
                config_json: {
                    ...(agentEditing?.config_json || {}),
                    enabled_agents,
                    timeframe: agentForm.timeframe,
                },
            };
            if (payload.capital_allocation) payload.capital_allocation = parseFloat(payload.capital_allocation);
            if (payload.max_position_value) payload.max_position_value = parseFloat(payload.max_position_value);
            if (payload.max_daily_loss) payload.max_daily_loss = parseFloat(payload.max_daily_loss);
            payload.broker_connection_id = payload.broker_connection_id || null;
            // Decision Timing — coerce blanks/non-numerics back to the 30s default
            // so the backend never sees `''` for an Integer column.
            const _cis = parseInt(payload.cycle_interval_seconds, 10);
            payload.cycle_interval_seconds = Number.isFinite(_cis) && _cis > 0 ? _cis : 30;

            if (agentEditing) {
                // execution_mode is locked at creation time — never send it on update.
                delete payload.execution_mode;
                const res = await axiosInstance.put(`/api/trade-execution/ai-agents/${agentEditing.id}`, payload);
                // Keep `selectedAgent` in sync with the saved record so the Live
                // Market Data auto-refresh useEffect picks up the new
                // `cycle_interval_seconds` (Decision Timing) immediately —
                // without this, `loadAgents()` refreshes the list but the
                // currently-selected reference stays stale and the refresh
                // interval keeps the old cadence.
                const updated = res?.data?.data;
                if (updated && selectedAgent?.id === agentEditing.id) {
                    setSelectedAgent(updated);
                }
                setSnackbar({ message: 'Agent updated', severity: 'success' });
            } else {
                await axiosInstance.post('/api/trade-execution/ai-agents', payload);
                setSnackbar({ message: 'Agent created with default prompt', severity: 'success' });
            }
            setAgentDialog(false);
            setAgentEditing(null);
            setAgentForm({ ...INITIAL_AGENT_FORM });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to save agent', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAgent = async (id) => {
        if (!window.confirm('Are you sure you want to delete this agent?')) return;
        try {
            await axiosInstance.delete(`/api/trade-execution/ai-agents/${id}`);
            setSnackbar({ message: 'Agent deleted', severity: 'success' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: 'Failed to delete agent', severity: 'error' });
        }
    };

    const handleLifecycle = async (id, action) => {
        try {
            await axiosInstance.post(`/api/trade-execution/ai-agents/${id}/${action}`);
            setSnackbar({ message: `Agent ${action}ed`, severity: 'success' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || `Failed to ${action} agent`, severity: 'error' });
        }
    };

    const handleRunCycle = async (id) => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${id}/run-cycle`);
            const data = res.data;
            const decisionCount = data.data?.decisions?.length || 0;
            setSnackbar({ message: data.message || `Cycle completed with ${decisionCount} decisions`, severity: 'success' });
            loadAgents();
            if (selectedAgent?.id === id && activeTab === 3) loadDecisions(id);
            if (selectedAgent?.id === id && activeTab === 4) { loadPerformance(id); loadCycles(id); }
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Cycle failed', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditAgent = (agent) => {
        setAgentEditing(agent);
        setAgentForm({
            name: agent.name || '',
            description: agent.description || '',
            execution_mode: agent.execution_mode || 'NO_LLM',
            symbol: agent.symbol || '',
            symbol_id: agent.symbol_id || '',
            broker_connection_id: agent.broker_connection_id || '',
            temperature: agent.temperature || 0.3,
            max_output_tokens: agent.max_output_tokens || 12000,
            cycle_interval_seconds: agent.cycle_interval_seconds || 30,
            timeframe: agent.config_json?.timeframe
                || secondsToTimeframe(agent.cycle_interval_seconds || 300),
            max_daily_cycles: agent.max_daily_cycles || 50,
            auto_execute: agent.auto_execute || false,
            capital_allocation: agent.capital_allocation || '',
            max_position_value: agent.max_position_value || '',
            max_daily_loss: agent.max_daily_loss || '',
            max_open_positions: agent.max_open_positions || 3,
            trading_start_time: agent.trading_start_time || '',
            trading_end_time: agent.trading_end_time || '',
            trading_timezone: agent.trading_timezone || 'America/Chicago',
            enabled_agents: {
                macro_strategist: agent.config_json?.enabled_agents?.macro_strategist ?? true,
                options_volatility: agent.config_json?.enabled_agents?.options_volatility ?? true,
                asset_strategist: agent.config_json?.enabled_agents?.asset_strategist ?? true,
            },
        });
        setAgentDialog(true);
    };

    // ==================== PROMPT HANDLERS ====================

    const handleSelectPrompt = (prompt) => {
        setSelectedPrompt(prompt);
        setPromptEditorValue(prompt.system_prompt || '');
        setDataTemplateValue(prompt.data_template || '');
        setPromptName(prompt.name || '');
        setPromptNotes(prompt.notes || '');
    };

    const handleSavePrompt = async () => {
        if (!selectedPrompt || !selectedAgent) return;
        setSubmitting(true);
        try {
            await axiosInstance.put(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/${selectedPrompt.id}`, {
                name: promptName,
                system_prompt: promptEditorValue,
                data_template: dataTemplateValue,
                notes: promptNotes,
            });
            setSnackbar({ message: 'Prompt saved', severity: 'success' });
            loadPrompts(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: 'Failed to save prompt', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreatePrompt = async () => {
        if (!selectedAgent) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts`, {
                name: 'New Prompt Version',
            });
            setSnackbar({ message: 'New prompt version created', severity: 'success' });
            loadPrompts(selectedAgent.id);
            handleSelectPrompt(res.data.data);
        } catch (err) {
            setSnackbar({ message: 'Failed to create prompt', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleLoadPresetPrompts = async () => {
        if (!selectedAgent) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/seed-presets`);
            setSnackbar({ message: res.data.message || 'Preset templates loaded', severity: 'success' });
            loadPrompts(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to load preset templates', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== PROMPT BUILDER — variable insertion & preset copy ====================

    const insertVariableInto = useCallback((field, varName) => {
        const isTemplate = field === 'data_template';
        const insertText = isTemplate ? `{${varName}}` : `\`${varName}\``;
        const ref = isTemplate ? dataTemplateRef : systemPromptRef;
        const el = ref.current;
        const currentValue = isTemplate ? dataTemplateValue : promptEditorValue;
        const setter = isTemplate ? setDataTemplateValue : setPromptEditorValue;

        const selStart = el?.selectionStart ?? currentValue.length;
        const selEnd = el?.selectionEnd ?? currentValue.length;
        const next = currentValue.slice(0, selStart) + insertText + currentValue.slice(selEnd);
        setter(next);

        requestAnimationFrame(() => {
            if (!el) return;
            const caret = selStart + insertText.length;
            el.focus();
            el.setSelectionRange(caret, caret);
        });
    }, [dataTemplateValue, promptEditorValue]);

    const insertVariable = useCallback((varName) => {
        insertVariableInto(focusedField || 'system_prompt', varName);
    }, [focusedField, insertVariableInto]);

    const handleVariableDragStart = (e, varName) => {
        e.dataTransfer.setData('text/variable', varName);
        e.dataTransfer.setData('text/plain', varName);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleTextareaDrop = (e, targetField) => {
        const varName = e.dataTransfer.getData('text/variable');
        if (!varName) return; // not a variable drag — let browser default handle
        e.preventDefault();
        setFocusedField(targetField);
        insertVariableInto(targetField, varName);
    };

    const loadPresetTemplates = useCallback(async () => {
        if (presetsLoaded) return;
        try {
            const res = await axiosInstance.get('/api/trade-execution/ai-agents/preset-templates');
            setPresetTemplates(res.data?.data?.presets || []);
            setPresetsLoaded(true);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to load preset templates', severity: 'error' });
        }
    }, [presetsLoaded]);

    const handleOpenPresetMenu = async (e) => {
        setPresetMenuAnchor(e.currentTarget);
        await loadPresetTemplates();
    };

    const handleCopyFromPreset = async (preset) => {
        setPresetMenuAnchor(null);
        if (!selectedAgent || !preset) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts`, {
                name: `${preset.name} (Custom)`,
                system_prompt: preset.system_prompt,
                notes: `Copied from preset: ${preset.name}`,
            });
            setSnackbar({ message: `Copied "${preset.name}" as a new draft`, severity: 'success' });
            await loadPrompts(selectedAgent.id);
            handleSelectPrompt(res.data.data);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to copy preset', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleActivatePrompt = async (promptId) => {
        if (!selectedAgent) return;
        try {
            await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/${promptId}/activate`);
            setSnackbar({ message: 'Prompt activated', severity: 'success' });
            loadPrompts(selectedAgent.id);
            loadAgents();
        } catch (err) {
            setSnackbar({ message: 'Failed to activate prompt', severity: 'error' });
        }
    };

    const handleDeletePrompt = async (promptId) => {
        if (!selectedAgent) return;
        if (!window.confirm('Are you sure you want to delete this prompt version?')) return;
        try {
            await axiosInstance.delete(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/${promptId}`);
            setSnackbar({ message: 'Prompt deleted', severity: 'success' });
            if (selectedPrompt?.id === promptId) setSelectedPrompt(null);
            loadPrompts(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to delete prompt', severity: 'error' });
        }
    };

    // ==================== POSITION SIZING HANDLERS ====================

    const handleSizingMultiplierChange = (idx, raw) => {
        const next = [...sizingMultipliers];
        const num = raw === '' ? 0 : Number(raw);
        next[idx] = Number.isFinite(num) && num >= 0 ? num : 0;
        setSizingMultipliers(next);
        setSizingDirty(true);
    };

    const handleSizingDivisorChange = (raw) => {
        const num = raw === '' ? 0 : Number(raw);
        setSizingDivisor(Number.isFinite(num) ? Math.max(1, Math.floor(num)) : 1);
        setSizingDirty(true);
    };

    const handleSizingReset = () => {
        setSizingDivisor(DEFAULT_POSITION_SIZING.divisor);
        setSizingMultipliers([...DEFAULT_POSITION_SIZING.multipliers]);
        setSizingDirty(true);
    };

    const handleSizingSave = async () => {
        if (!selectedAgent) return;
        if (sizingDivisor <= 0 || sizingMultipliers.length !== 4 || sizingMultipliers.some(m => m < 0)) {
            setSnackbar({ message: 'Divisor must be > 0 and all 4 multipliers must be ≥ 0.', severity: 'error' });
            return;
        }
        const sumMult = sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0);
        if (sumMult > sizingDivisor) {
            setSnackbar({
                message: `Total of all 4 trades (${sumMult}) cannot exceed the Divisor (${sizingDivisor}). That would over-allocate capital.`,
                severity: 'error',
            });
            return;
        }
        setSizingSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/trade-execution/ai-agents/${selectedAgent.id}`, {
                position_sizing: { divisor: sizingDivisor, multipliers: sizingMultipliers },
            });
            const updated = res?.data?.data;
            if (updated) {
                setSelectedAgent(updated);
                setAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
            }
            setSizingDirty(false);
            setSnackbar({ message: 'Position sizing saved', severity: 'success' });
        } catch (err) {
            setSnackbar({
                message: err.response?.data?.message || 'Failed to save position sizing',
                severity: 'error',
            });
        } finally {
            setSizingSubmitting(false);
        }
    };

    // ==================== RENDER HELPERS ====================

    const renderSkeletonRows = (cols) => (
        [...Array(3)].map((_, i) => (
            <TableRow key={i}>
                {[...Array(cols)].map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                ))}
            </TableRow>
        ))
    );

    // ==================== TAB 0: AGENT MANAGEMENT ====================

    const renderAgentManagement = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                    AI Trading Agents
                </Typography>
                {canAdd && (
                    <Button variant="contained" startIcon={<MuiIcons.Add />} onClick={() => { setAgentEditing(null); setAgentForm({ ...INITIAL_AGENT_FORM }); setAgentDialog(true); }}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        Create Agent
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={headCellSx}>Name</TableCell>
                            <TableCell sx={headCellSx}>Symbol</TableCell>
                            <TableCell sx={headCellSx}>Status</TableCell>
                            <TableCell sx={headCellSx}>Last Cycle</TableCell>
                            <TableCell sx={headCellSx}>Cycles</TableCell>
                            <TableCell sx={headCellSx}>Decisions</TableCell>
                            <TableCell sx={headCellSx}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agentsLoading ? renderSkeletonRows(7) : agents.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={bodyCellSx}>No agents created yet</TableCell></TableRow>
                        ) : agents.map((agent) => (
                            <TableRow key={agent.id} hover sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setSelectedAgent(agent);
                                    if (activeTab === 0) setActiveTab(agent.execution_mode === 'NO_LLM' ? 3 : 2);
                                }}>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{agent.name}</TableCell>
                                <TableCell sx={bodyCellSx}><Chip label={agent.symbol} size="small" variant="outlined" /></TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip label={AIAgentStatusLabels[agent.status] || agent.status}
                                        color={AIAgentStatusColors[agent.status] || 'default'} size="small" />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    {agent.last_cycle_at ? new Date(agent.last_cycle_at).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{agent.total_cycles || 0}</TableCell>
                                <TableCell sx={bodyCellSx}>{agent.total_decisions || 0}</TableCell>
                                <TableCell sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {agent.status !== 'ACTIVE' && (
                                            <Tooltip title="Start"><IconButton size="small" color="success" onClick={() => handleLifecycle(agent.id, 'start')}><MuiIcons.PlayArrow fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {agent.status === 'ACTIVE' && (
                                            <Tooltip title="Pause"><IconButton size="small" color="warning" onClick={() => handleLifecycle(agent.id, 'pause')}><MuiIcons.Pause fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {agent.status !== 'STOPPED' && (
                                            <Tooltip title="Stop"><IconButton size="small" color="error" onClick={() => handleLifecycle(agent.id, 'stop')}><MuiIcons.Stop fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        <Tooltip title="Run Cycle"><IconButton size="small" color="primary" onClick={() => handleRunCycle(agent.id)} disabled={submitting}><MuiIcons.PlayCircleOutline fontSize="small" /></IconButton></Tooltip>
                                        {canEdit && (
                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEditAgent(agent)}><MuiIcons.Edit fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteAgent(agent.id)}><MuiIcons.Delete fontSize="small" /></IconButton></Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination component="div" count={agentsTotal} page={agentsPage} rowsPerPage={agentsPerPage}
                    onPageChange={(_, p) => setAgentsPage(p)} onRowsPerPageChange={(e) => { setAgentsPerPage(parseInt(e.target.value)); setAgentsPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]} sx={{ fontFamily }} />
            </TableContainer>
        </Box>
    );

    // ==================== TAB 1: PROMPT EDITOR ====================

    const renderPromptEditor = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to edit its prompts.</Alert>;

        const isNoLLM = selectedAgent.execution_mode === 'NO_LLM';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: 'calc(100vh - 280px)' }}>
                {isNoLLM && (
                    <Alert severity="info" icon={<MuiIcons.Lock />} sx={{ fontFamily, fontSize: '0.82rem' }}>
                        This agent runs in <strong>No LLM</strong> mode — trading decisions come from the
                        Pure Code Execution & Risk Router. Prompts are read-only here for reference; saving,
                        activating, creating, and deleting are disabled.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
                {/* Left panel: prompt versions */}
                <Paper sx={{ width: 280, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Versions</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={isNoLLM ? 'Disabled in No LLM mode' : 'Load Preset Strategy Templates'}>
                                <span>
                                    <IconButton size="small" onClick={handleLoadPresetPrompts} disabled={submitting || isNoLLM}>
                                        <MuiIcons.LibraryAdd fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={isNoLLM ? 'Disabled in No LLM mode' : 'Create New Version'}>
                                <span>
                                    <IconButton size="small" onClick={handleCreatePrompt} disabled={submitting || isNoLLM}>
                                        <MuiIcons.Add fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    {promptsLoading ? <Skeleton variant="rectangular" height={100} /> : prompts.map((p) => (
                        <Paper key={p.id} onClick={() => handleSelectPrompt(p)}
                            sx={{
                                p: 1.5, mb: 1, cursor: 'pointer', borderRadius: '8px',
                                border: `1px solid ${selectedPrompt?.id === p.id ? ACCENT : '#e2e8f0'}`,
                                bgcolor: selectedPrompt?.id === p.id ? `${ACCENT}08` : 'transparent',
                                '&:hover': { borderColor: ACCENT },
                            }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>{p.name}</Typography>
                                {p.version_status !== 'ACTIVE' && !p.is_default && !isNoLLM && (
                                    <Tooltip title="Delete prompt">
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeletePrompt(p.id); }}
                                            sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                            <MuiIcons.DeleteOutline sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                                <Chip label={p.version_label} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                <Chip label={PromptVersionStatusLabels[p.version_status] || p.version_status}
                                    color={PromptVersionStatusColors[p.version_status] || 'default'} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            </Box>
                            {p.version_status !== 'ACTIVE' ? (
                                <Button variant="contained" size="small" startIcon={<MuiIcons.CheckCircleOutline sx={{ fontSize: '0.85rem' }} />}
                                    disabled={isNoLLM}
                                    onClick={(e) => { e.stopPropagation(); handleActivatePrompt(p.id); }}
                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.7rem', mt: 1, borderRadius: '6px',
                                        bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, px: 1.5, py: 0.3 }}>
                                    Activate
                                </Button>
                            ) : (
                                <Chip icon={<MuiIcons.CheckCircle sx={{ fontSize: '0.8rem' }} />} label="Active"
                                    size="small" sx={{ mt: 1, bgcolor: '#dcfce7', color: '#16a34a', fontSize: '0.7rem', fontWeight: 600, height: 22, fontFamily }} />
                            )}
                        </Paper>
                    ))}
                </Paper>

                {/* Middle panel: editor */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {!selectedPrompt ? (
                        <Alert severity="info" sx={{ fontFamily }}>Select a prompt version from the left panel to edit.</Alert>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <TextField size="small" label="Prompt Name" value={promptName} onChange={(e) => setPromptName(e.target.value)}
                                    InputProps={{ readOnly: isNoLLM }}
                                    sx={{ flex: 1, minWidth: 180, '& .MuiInputBase-root': { fontFamily } }} />
                                <TextField size="small" label="Notes" value={promptNotes} onChange={(e) => setPromptNotes(e.target.value)}
                                    InputProps={{ readOnly: isNoLLM }}
                                    sx={{ flex: 1, minWidth: 180, '& .MuiInputBase-root': { fontFamily } }} />
                                <Button variant="outlined" size="small" onClick={handleOpenPresetMenu} disabled={submitting || isNoLLM}
                                    startIcon={<MuiIcons.ContentCopy sx={{ fontSize: '1rem' }} />}
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: '#8b5cf608' } }}>
                                    Copy from Preset
                                </Button>
                                <Menu anchorEl={presetMenuAnchor} open={Boolean(presetMenuAnchor)} onClose={() => setPresetMenuAnchor(null)}>
                                    {presetTemplates.length === 0 ? (
                                        <MenuItem disabled sx={{ fontFamily, fontSize: '0.85rem' }}>
                                            {presetsLoaded ? 'No presets available' : 'Loading presets...'}
                                        </MenuItem>
                                    ) : presetTemplates.map((preset) => (
                                        <MenuItem key={preset.name} onClick={() => handleCopyFromPreset(preset)}
                                            sx={{ fontFamily, fontSize: '0.85rem', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>{preset.name}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', whiteSpace: 'normal', maxWidth: 380 }}>
                                                {preset.description}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <Button variant="contained" onClick={handleSavePrompt} disabled={submitting || isNoLLM}
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                    Save
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>System Prompt</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: focusedField === 'system_prompt' ? ACCENT : '#94a3b8' }}>
                                    {focusedField === 'system_prompt' ? 'Insert target: backticked reference `var`' : 'Click in to set as insert target'}
                                </Typography>
                            </Box>
                            <TextField multiline fullWidth value={promptEditorValue}
                                onChange={(e) => setPromptEditorValue(e.target.value)}
                                onFocus={() => setFocusedField('system_prompt')}
                                inputRef={systemPromptRef}
                                InputProps={{ readOnly: isNoLLM }}
                                inputProps={{
                                    onDragOver: (e) => e.preventDefault(),
                                    onDrop: (e) => handleTextareaDrop(e, 'system_prompt'),
                                }}
                                sx={{
                                    flex: 1, mb: 1,
                                    '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.8rem', alignItems: 'flex-start' },
                                    '& .MuiInputBase-input': { overflow: 'auto' },
                                    '& .MuiOutlinedInput-root': {
                                        ...(focusedField === 'system_prompt' ? { '& fieldset': { borderColor: ACCENT, borderWidth: 2 } } : {}),
                                    },
                                }} />
                            <Button size="small" onClick={() => setShowDataTemplate(!showDataTemplate)}
                                startIcon={showDataTemplate ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                                sx={{ fontFamily, textTransform: 'none', color: '#64748b', alignSelf: 'flex-start', mb: 0.5 }}>
                                Data Template
                            </Button>
                            <Collapse in={showDataTemplate}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: focusedField === 'data_template' ? ACCENT : '#94a3b8' }}>
                                        {focusedField === 'data_template' ? 'Insert target: placeholder {var}' : 'Click in to set as insert target'}
                                    </Typography>
                                </Box>
                                <TextField multiline fullWidth rows={8} value={dataTemplateValue}
                                    onChange={(e) => setDataTemplateValue(e.target.value)}
                                    onFocus={() => setFocusedField('data_template')}
                                    inputRef={dataTemplateRef}
                                    InputProps={{ readOnly: isNoLLM }}
                                    inputProps={{
                                        onDragOver: (e) => e.preventDefault(),
                                        onDrop: (e) => handleTextareaDrop(e, 'data_template'),
                                    }}
                                    sx={{
                                        '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.8rem' },
                                        '& .MuiOutlinedInput-root': {
                                            ...(focusedField === 'data_template' ? { '& fieldset': { borderColor: ACCENT, borderWidth: 2 } } : {}),
                                        },
                                    }} />
                            </Collapse>
                        </>
                    )}
                </Box>

                {/* Right column: two independently collapsible Papers stacked vertically.
                    The chip list inside Variables has its own maxHeight + internal scroll
                    so it never squeezes Position Sizing off-screen, regardless of how many
                    variable groups are visible. The outer Box scrolls only when both
                    sections together exceed the viewport. */}
                {selectedPrompt && (
                    <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflow: 'auto' }}>
                        {/* — Variables Paper (toggle) — */}
                        <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                            <Button size="small" onClick={() => {
                                    const next = !showVariables;
                                    setShowVariables(next);
                                    if (next) setShowSizingEditor(false);
                                }}
                                startIcon={showVariables ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                                sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                                Variables
                            </Button>
                            <Collapse in={showVariables}>
                                <Box sx={{ mt: 1, maxHeight: '40vh', overflow: 'auto', pr: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mb: 1 }}>
                                        Click or drag into the focused field. System Prompt → <code>{'`var`'}</code>, Data Template → <code>{'{var}'}</code>.
                                    </Typography>
                                    <TextField size="small" placeholder="Search variables…" value={variableSearch}
                                        onChange={(e) => setVariableSearch(e.target.value)}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><MuiIcons.Search sx={{ fontSize: '1rem', color: '#94a3b8' }} /></InputAdornment>,
                                        }}
                                        sx={{ mb: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' } }} />
                                    <Box>
                                        {(() => {
                                            const enabledAgents = {
                                                macro_strategist: selectedAgent?.config_json?.enabled_agents?.macro_strategist ?? true,
                                                options_volatility: selectedAgent?.config_json?.enabled_agents?.options_volatility ?? true,
                                                asset_strategist: selectedAgent?.config_json?.enabled_agents?.asset_strategist ?? true,
                                            };
                                            return PROMPT_VARIABLES.map((group) => {
                                                const q = variableSearch.trim().toLowerCase();
                                                const filtered = group.vars.filter(v => {
                                                    if (v.agent && !enabledAgents[v.agent]) return false;
                                                    if (!q) return true;
                                                    return v.name.toLowerCase().includes(q) || v.label.toLowerCase().includes(q);
                                                });
                                                if (filtered.length === 0) return null;
                                                return (
                                                    <Box key={group.category} sx={{ mb: 1.25 }}>
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                                                            letterSpacing: '0.04em', color: group.color, mb: 0.5, pl: 0.25,
                                                        }}>
                                                            {group.category}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {filtered.map((v) => (
                                                                <Tooltip key={v.name} title={v.label} placement="top">
                                                                    <Chip
                                                                        label={v.name}
                                                                        size="small"
                                                                        draggable
                                                                        onDragStart={(e) => handleVariableDragStart(e, v.name)}
                                                                        onClick={() => insertVariable(v.name)}
                                                                        sx={{
                                                                            fontFamily: 'monospace', fontSize: '0.7rem', height: 22,
                                                                            cursor: 'grab', bgcolor: `${group.color}14`, color: group.color,
                                                                            border: `1px solid ${group.color}30`,
                                                                            '&:hover': { bgcolor: `${group.color}26` },
                                                                            '&:active': { cursor: 'grabbing' },
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                );
                                            });
                                        })()}
                                    </Box>
                                </Box>
                            </Collapse>
                        </Paper>

                        {/* — Position Sizing Paper (collapsible) — */}
                        <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                            <Button size="small" onClick={() => {
                                    const next = !showSizingEditor;
                                    setShowSizingEditor(next);
                                    if (next) setShowVariables(false);
                                }}
                                startIcon={showSizingEditor ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                                endIcon={sizingDirty ? (
                                    <Chip label="unsaved" size="small"
                                        sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#fef3c7', color: '#a16207', fontFamily }} />
                                ) : null}
                                sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                                Position Sizing
                            </Button>
                            <Collapse in={showSizingEditor}>
                                <Box sx={{ mt: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fafbfc' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mb: 1 }}>
                                        Override the default <strong>1-2-4-8 / 15-part</strong> Martingale ladder. Recomputes
                                        {' '}<code>sizing_t1_shares</code>..<code>sizing_t4_shares</code> and prefixes the active
                                        prompt with an override directive when non-default.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <TextField size="small" type="number" label="Divisor" value={sizingDivisor}
                                            onChange={(e) => handleSizingDivisorChange(e.target.value)}
                                            InputProps={{ readOnly: isNoLLM, inputProps: { min: 1, step: 1 } }}
                                            sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' } }} />
                                        <TextField size="small" label="Current Price"
                                            disabled
                                            value={(() => {
                                                const cp = marketData?.current_price ?? marketData?.raw?.current_price;
                                                return cp != null ? `$${Number(cp).toFixed(2)}` : 'Loading…';
                                            })()}
                                            sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' }, '& .Mui-disabled': { WebkitTextFillColor: '#1e293b !important', color: '#1e293b !important' } }} />
                                    </Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#64748b', mb: 1 }}>
                                        Base Unit U = capital / divisor &nbsp;·&nbsp;
                                        Capital: <strong>${Number(selectedAgent?.capital_allocation || 0).toLocaleString()}</strong>
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                        {sizingMultipliers.map((mult, idx) => {
                                            const cap = Number(selectedAgent?.capital_allocation || 0);
                                            const baseUnit = sizingDivisor > 0 ? cap / sizingDivisor : 0;
                                            const dollars = baseUnit * Number(mult || 0);
                                            const currentPrice = Number(marketData?.current_price ?? marketData?.raw?.current_price ?? 0);
                                            const isSpotCrypto = Boolean(marketData?.raw?.is_spot_crypto);
                                            const rawShares = currentPrice > 0 ? dollars / currentPrice : 0;
                                            // Spot crypto trades fractionally (BTC/ETH/etc); equities round down to whole shares.
                                            const shares = isSpotCrypto
                                                ? Number(rawShares.toFixed(6))
                                                : Math.floor(rawShares);
                                            const sharesDisplay = isSpotCrypto
                                                ? shares.toLocaleString(undefined, { maximumFractionDigits: 6 })
                                                : shares.toLocaleString();
                                            return (
                                                <Box key={idx} sx={{ p: 0.75, border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>
                                                        Trade {idx + 1}
                                                    </Typography>
                                                    <TextField size="small" type="number" value={mult}
                                                        onChange={(e) => handleSizingMultiplierChange(idx, e.target.value)}
                                                        InputProps={{ readOnly: isNoLLM, inputProps: { min: 0, step: 0.5 } }}
                                                        fullWidth
                                                        sx={{ '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' } }} />
                                                    <Box sx={{ mt: 0.5, fontFamily, fontSize: '0.65rem', color: '#475569', lineHeight: 1.4 }}>
                                                        <div>${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                                        <div>{sharesDisplay} {isSpotCrypto ? 'units' : 'shares'}</div>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {(() => {
                                        const sumMult = sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0);
                                        const overallocated = sumMult > sizingDivisor;
                                        return (
                                            <Box sx={{
                                                mt: 1, p: 0.75, borderRadius: '6px',
                                                bgcolor: overallocated ? '#fef2f2' : '#f1f5f9',
                                                border: `1px solid ${overallocated ? '#fecaca' : '#e2e8f0'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                fontFamily, fontSize: '0.7rem',
                                            }}>
                                                <span style={{ color: overallocated ? '#b91c1c' : '#475569', fontWeight: 600 }}>
                                                    Total of trades: {sumMult} / {sizingDivisor}
                                                </span>
                                                {overallocated && (
                                                    <span style={{ color: '#b91c1c' }}>
                                                        Over by {sumMult - sizingDivisor} — reduce a trade or raise the Divisor.
                                                    </span>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 1 }}>
                                        <Button size="small" variant="outlined" onClick={handleSizingReset}
                                            disabled={isNoLLM || sizingSubmitting}
                                            sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1 }}>
                                            Reset
                                        </Button>
                                        <Button size="small" variant="contained" onClick={handleSizingSave}
                                            disabled={isNoLLM || sizingSubmitting || !sizingDirty
                                                || sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0) > sizingDivisor}
                                            sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                            {sizingSubmitting ? 'Saving…' : 'Save'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Paper>
                    </Box>
                )}
                </Box>
            </Box>
        );
    };

    // ==================== TAB 2: DECISION LOG ====================

    const renderDecisionLog = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view its decisions.</Alert>;

        const sourceChip = (src) => {
            if (src === 'PURE_CODE_ROUTER') {
                return <Chip label="PURE CODE" size="small" sx={{
                    fontFamily, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: '#ede9fe', color: '#6d28d9', height: 20,
                }} />;
            }
            if (src === 'LLM') {
                return <Chip label="LLM" size="small" sx={{
                    fontFamily, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: '#fef3c7', color: '#b45309', height: 20,
                }} />;
            }
            return <Chip label="—" size="small" variant="outlined" sx={{ fontFamily, fontSize: '0.65rem', height: 20 }} />;
        };

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                        Decision Log - {selectedAgent.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Refresh decisions">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => loadDecisions(selectedAgent.id)}
                                    disabled={decisionsLoading}
                                    sx={{ color: '#3b82f6' }}
                                >
                                    <MuiIcons.Refresh fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ fontFamily }}>Source</InputLabel>
                            <Select
                                label="Source"
                                value={decisionsSourceFilter}
                                onChange={(e) => { setDecisionsSourceFilter(e.target.value); setDecisionsPage(0); }}
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="ALL" sx={{ fontFamily }}>All sources</MenuItem>
                                <MenuItem value="LLM" sx={{ fontFamily }}>LLM only</MenuItem>
                                <MenuItem value="PURE_CODE_ROUTER" sx={{ fontFamily }}>Pure Code only</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx} width={30}></TableCell>
                                <TableCell sx={headCellSx}>Time</TableCell>
                                <TableCell sx={headCellSx}>Source</TableCell>
                                <TableCell sx={headCellSx}>Action</TableCell>
                                <TableCell sx={headCellSx}>Symbol</TableCell>
                                <TableCell sx={headCellSx}>Direction</TableCell>
                                <TableCell sx={headCellSx}>Qty</TableCell>
                                <TableCell sx={headCellSx}>Price</TableCell>
                                <TableCell sx={headCellSx}>SL / TP</TableCell>
                                <TableCell sx={headCellSx}>Confidence</TableCell>
                                <TableCell sx={headCellSx}>Phase</TableCell>
                                <TableCell sx={headCellSx}>Executed</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {decisionsLoading ? renderSkeletonRows(12) : decisions.length === 0 ? (
                                <TableRow><TableCell colSpan={12} align="center" sx={bodyCellSx}>No decisions yet. Run a cycle to generate decisions.</TableCell></TableRow>
                            ) : decisions.map((d) => (
                                <React.Fragment key={d.id}>
                                    <TableRow hover>
                                        <TableCell sx={bodyCellSx}>
                                            <IconButton size="small" onClick={() => setExpandedDecision(expandedDecision === d.id ? null : d.id)}>
                                                {expandedDecision === d.id ? <MuiIcons.ExpandLess fontSize="small" /> : <MuiIcons.ExpandMore fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{d.created_at ? new Date(d.created_at).toLocaleString() : '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{sourceChip(d.decision_source)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={AIAgentDecisionActionLabels[d.action] || d.action}
                                                color={AIAgentDecisionActionColors[d.action] || 'default'} size="small" />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{d.symbol}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.direction && <Chip label={d.direction} size="small" variant="outlined"
                                                color={d.direction === 'LONG' ? 'success' : 'error'} />}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{d.quantity || '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{d.price ? `$${d.price.toFixed(2)}` : '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.stop_loss ? `$${d.stop_loss.toFixed(2)}` : '-'} / {d.take_profit ? `$${d.take_profit.toFixed(2)}` : '-'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.confidence_score != null ? (
                                                <Chip label={`${(d.confidence_score * 100).toFixed(0)}%`} size="small" variant="outlined"
                                                    color={d.confidence_score >= 0.7 ? 'success' : d.confidence_score >= 0.4 ? 'warning' : 'error'} />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}><Chip label={d.phase || '-'} size="small" variant="outlined" /></TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.was_executed ? <MuiIcons.CheckCircle fontSize="small" color="success" /> : <MuiIcons.RadioButtonUnchecked fontSize="small" color="disabled" />}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={12} sx={{ p: 0, borderBottom: expandedDecision === d.id ? undefined : 'none' }}>
                                            <Collapse in={expandedDecision === d.id}>
                                                <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>Reasoning:</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
                                                        {d.reasoning || 'No reasoning provided'}
                                                    </Typography>
                                                    {d.execution_error && (
                                                        <Alert severity="error" sx={{ mt: 1, fontFamily, fontSize: '0.8rem' }}>{d.execution_error}</Alert>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination component="div" count={decisionsTotal} page={decisionsPage} rowsPerPage={decisionsPerPage}
                        onPageChange={(_, p) => setDecisionsPage(p)} onRowsPerPageChange={(e) => { setDecisionsPerPage(parseInt(e.target.value)); setDecisionsPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]} sx={{ fontFamily }} />
                </TableContainer>
            </Box>
        );
    };

    // ==================== TAB 3: PERFORMANCE ====================

    const renderPerformance = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view performance.</Alert>;

        return (
            <Box>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>
                    Performance - {selectedAgent.name}
                </Typography>

                {performanceLoading ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: '16px' }} /> : performance && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Loop} title="Total Cycles" value={performance.total_cycles} color="#3b82f6" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.CheckCircle} title="Success Rate" value={`${performance.cycle_success_rate}%`} color="#10b981" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Psychology} title="Decisions" value={performance.total_decisions} color="#8b5cf6" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.GppGood} title="Avg Confidence" value={`${(performance.avg_confidence * 100).toFixed(0)}%`} color="#f59e0b" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.AttachMoney} title="LLM Cost" value={`$${performance.total_llm_cost.toFixed(4)}`} color="#ef4444" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Bolt} title="Executed" value={performance.executed_decisions} color="#06b6d4" />
                            </Grid>
                        </Grid>

                        {/* Action Breakdown */}
                        {performance.action_breakdown && Object.keys(performance.action_breakdown).length > 0 && (
                            <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>Action Breakdown</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(performance.action_breakdown).map(([action, count]) => (
                                        <Chip key={action} label={`${AIAgentDecisionActionLabels[action] || action}: ${count}`}
                                            color={AIAgentDecisionActionColors[action] || 'default'} variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {/* Phase Breakdown */}
                        {performance.phase_breakdown && Object.keys(performance.phase_breakdown).length > 0 && (
                            <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>Phase Breakdown</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(performance.phase_breakdown).map(([phase, count]) => (
                                        <Chip key={phase} label={`${phase}: ${count}`} variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </>
                )}

                {/* Recent Cycles */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Recent Cycles</Typography>
                    <Tooltip title="Refresh performance & cycles">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => { loadPerformance(selectedAgent.id); loadCycles(selectedAgent.id); }}
                                disabled={performanceLoading || cyclesLoading}
                                sx={{ color: '#3b82f6' }}
                            >
                                <MuiIcons.Refresh fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx}>#</TableCell>
                                <TableCell sx={headCellSx}>Status</TableCell>
                                <TableCell sx={headCellSx}>Started</TableCell>
                                <TableCell sx={headCellSx}>Duration</TableCell>
                                <TableCell sx={headCellSx}>Tokens</TableCell>
                                <TableCell sx={headCellSx}>Cost</TableCell>
                                <TableCell sx={headCellSx}>Model</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cyclesLoading ? renderSkeletonRows(7) : cycles.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={bodyCellSx}>No cycles yet</TableCell></TableRow>
                            ) : cycles.map((c) => (
                                <TableRow key={c.id} hover>
                                    <TableCell sx={bodyCellSx}>{c.cycle_number}</TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Chip label={CycleStatusLabels[c.status] || c.status} color={CycleStatusColors[c.status] || 'default'} size="small" />
                                            {c.error_message && c.status !== 'KILL_SWITCH' && (
                                                <Tooltip
                                                    title={
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxWidth: 480 }}>
                                                            {c.error_message}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="right"
                                                >
                                                    <MuiIcons.ErrorOutline sx={{ fontSize: 16, color: '#ef4444', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {c.status === 'KILL_SWITCH' && (() => {
                                                const ks = c.kill_switches_triggered;
                                                const parts = [];
                                                if (ks?.details) parts.push(ks.details);
                                                if (Array.isArray(ks?.list) && ks.list.length > 0) {
                                                    parts.push(`Triggered: ${ks.list.join(', ')}`);
                                                }
                                                if (parts.length === 0 && c.error_message) parts.push(c.error_message);
                                                const tooltipText = parts.join('\n\n') || 'Kill switch triggered (no details recorded)';
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxWidth: 480 }}>
                                                                {tooltipText}
                                                            </Typography>
                                                        }
                                                        arrow
                                                        placement="right"
                                                    >
                                                        <MuiIcons.ErrorOutline sx={{ fontSize: 16, color: '#f59e0b', cursor: 'help' }} />
                                                    </Tooltip>
                                                );
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>{c.started_at ? new Date(c.started_at).toLocaleString() : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.duration_ms ? `${(c.duration_ms / 1000).toFixed(1)}s` : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{(c.input_tokens || 0) + (c.output_tokens || 0)}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.total_cost ? `$${c.total_cost.toFixed(4)}` : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.llm_model_used || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    // ==================== AGENT DIALOG ====================

    // ==================== TAB 4: LIVE MARKET DATA ====================

    const handleFetchCrypto = async () => {
        setFetchingCrypto(true);
        try {
            await axiosInstance.post('/api/crypto-data/arbitrage/fetch');
            setSnackbar({ message: 'Crypto arbitrage data fetched', severity: 'success' });
            if (selectedAgent) loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch crypto data', severity: 'error' });
        } finally {
            setFetchingCrypto(false);
        }
    };

    const handleFetchL2 = async () => {
        if (!selectedAgent) return;
        setFetchingL2(true);
        try {
            await axiosInstance.post('/api/crypto-data/l2-order-book/fetch', { symbol: selectedAgent.symbol });
            setSnackbar({ message: 'L2 order book data fetched', severity: 'success' });
            loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch L2 data', severity: 'error' });
        } finally {
            setFetchingL2(false);
        }
    };

    const handleFetchMacro = async () => {
        setFetchingMacro(true);
        try {
            await axiosInstance.post('/api/market-data-ingestion/macro/fetch-all');
            setSnackbar({ message: 'Fed macro data fetched successfully', severity: 'success' });
            if (selectedAgent) loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch macro data', severity: 'error' });
        } finally {
            setFetchingMacro(false);
        }
    };

    const DataFieldRow = ({ label, value, format, naReason }) => {
        const isNA = value === 'N/A' || value === null || value === undefined
            || (typeof value === 'string' && value.includes('N/A'))
            || (typeof value === 'number' && value === 0 && format === 'price');
        const isZero = typeof value === 'number' && value === 0;

        let displayValue = value;
        if (isNA) {
            displayValue = typeof value === 'string' && value.includes('N/A') ? value : 'N/A';
        } else if (typeof value === 'boolean') {
            displayValue = value ? 'TRUE' : 'FALSE';
        } else if ((format === 'price' || format === 'currency') && typeof value === 'number') {
            displayValue = `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        } else if (format === 'large_number' && typeof value === 'number') {
            displayValue = value >= 1e12 ? `$${(value / 1e12).toFixed(2)}T`
                : value >= 1e9 ? `$${(value / 1e9).toFixed(2)}B`
                : value >= 1e6 ? `$${(value / 1e6).toFixed(2)}M`
                : `$${value.toLocaleString()}`;
        } else if (format === 'percent' && typeof value === 'number') {
            displayValue = `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
        } else if (format === 'number' && typeof value === 'number') {
            displayValue = value.toFixed(4);
        } else if (format === 'integer' && typeof value === 'number') {
            displayValue = value.toString();
        }

        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, px: 1.5, '&:hover': { bgcolor: '#f8fafc' }, borderRadius: '6px' }}>
                <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{
                        fontFamily, fontSize: '0.85rem', fontWeight: 600,
                        color: isNA ? '#ef4444' : isZero ? '#94a3b8' : (format === 'text' && value === 'GREEN') ? '#10b981' : (format === 'text' && value === 'RED') ? '#ef4444' : '#1e293b',
                    }}>
                        {displayValue}
                    </Typography>
                    {isNA && naReason && (
                        <Tooltip
                            title={naReason}
                            placement="left"
                            arrow
                            componentsProps={{
                                tooltip: { sx: { fontFamily, fontSize: '0.72rem', maxWidth: 320, lineHeight: 1.4 } },
                            }}
                        >
                            <MuiIcons.InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                        </Tooltip>
                    )}
                </Box>
            </Box>
        );
    };

    const renderLiveMarketData = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view its market data.</Alert>;

        const categories = marketData?.categories || {};

        return (
            <Box>
                {/* Header with actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                            Live Market Data - {selectedAgent.name} ({selectedAgent.symbol})
                        </Typography>
                        {marketData?.timestamp && (
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                Last snapshot: {marketData.timestamp}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.CurrencyBitcoin />}
                            onClick={handleFetchCrypto} disabled={fetchingCrypto}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#f59e0b', color: '#f59e0b' }}>
                            {fetchingCrypto ? 'Fetching...' : 'Fetch Crypto Data'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.MenuBook />}
                            onClick={handleFetchL2} disabled={fetchingL2}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6' }}>
                            {fetchingL2 ? 'Fetching...' : 'Fetch L2 Order Book'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.AccountBalance />}
                            onClick={handleFetchMacro} disabled={fetchingMacro}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#059669', color: '#059669' }}>
                            {fetchingMacro ? 'Fetching...' : 'Fetch Fed Macro'}
                        </Button>
                        <Button variant="contained" size="small" startIcon={<MuiIcons.Refresh />}
                            onClick={() => refreshMarketData(selectedAgent.id)} disabled={marketDataLoading}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                            {marketDataLoading ? 'Refreshing...' : 'Refresh All'}
                        </Button>
                    </Box>
                </Box>

                {marketDataLoading && !marketData ? (
                    <Grid container spacing={2}>
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <Grid item xs={12} md={6} lg={4} key={i}>
                                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : !marketData ? (
                    <Alert severity="info" sx={{ fontFamily }}>No market data loaded. Click "Refresh All" to load.</Alert>
                ) : (
                    <>
                        {(() => {
                            const optionsDeskKeys = ['options_data', 'strangle_geometry', 'options_volatility_agent'];
                            const fedMacroKeys = ['current_fed_macro', 'prev_fed_macro', 'macro_strategist_agent'];
                            const cryptoDivergenceKeys = ['asset_classification', 'crypto_arbitrage', 'asset_strategist_agent'];
                            const vwapExecutionKeys = ['technical_data', 'order_flow', 'execution_gates', 'vwap_execution_agent'];
                            const sectionedKeys = [...optionsDeskKeys, ...fedMacroKeys, ...cryptoDivergenceKeys, ...vwapExecutionKeys];
                            const enabledAgents = {
                                macro_strategist: selectedAgent?.config_json?.enabled_agents?.macro_strategist ?? true,
                                options_volatility: selectedAgent?.config_json?.enabled_agents?.options_volatility ?? true,
                                asset_strategist: selectedAgent?.config_json?.enabled_agents?.asset_strategist ?? true,
                            };
                            const optionsDeskEntries = enabledAgents.options_volatility
                                ? optionsDeskKeys.map(k => [k, categories[k]]).filter(([, cat]) => cat)
                                : [];
                            const fedMacroEntries = enabledAgents.macro_strategist
                                ? fedMacroKeys.map(k => [k, categories[k]]).filter(([, cat]) => cat)
                                : [];
                            const cryptoDivergenceEntries = enabledAgents.asset_strategist
                                ? cryptoDivergenceKeys.map(k => [k, categories[k]]).filter(([, cat]) => cat)
                                : [];
                            const vwapExecutionEntries = vwapExecutionKeys
                                .map(k => [k, categories[k]])
                                .filter(([, cat]) => cat);
                            const otherEntries = Object.entries(categories)
                                .filter(([k]) => !sectionedKeys.includes(k));

                            const renderCategoryCard = ([key, category]) => {
                                const fields = category.fields || {};
                                const allNA = Object.values(fields).every(f =>
                                    f.value === 'N/A' || f.value === null || f.value === undefined
                                    || (typeof f.value === 'string' && f.value.includes('N/A'))
                                );
                                const hasData = Object.values(fields).some(f =>
                                    f.value !== null && f.value !== undefined && f.value !== 0
                                    && f.value !== 'N/A' && !(typeof f.value === 'string' && f.value.includes('N/A'))
                                );

                                return (
                                    <Grid item xs={12} md={6} lg={4} key={key}>
                                        <Paper sx={{
                                            borderRadius: '12px',
                                            border: `1px solid ${allNA ? '#fecaca' : hasData ? '#bbf7d0' : '#e2e8f0'}`,
                                            boxShadow: 'none', overflow: 'hidden', height: '100%',
                                        }}>
                                            <Box sx={{
                                                px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1,
                                                bgcolor: allNA ? '#fef2f2' : hasData ? '#f0fdf4' : '#f8fafc',
                                                borderBottom: '1px solid #e2e8f0',
                                            }}>
                                                <Box sx={{
                                                    width: 8, height: 8, borderRadius: '50%',
                                                    bgcolor: allNA ? '#ef4444' : hasData ? '#22c55e' : '#94a3b8',
                                                }} />
                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                    {category.label}
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: category.last_updated ? '#94a3b8' : '#ef4444', ml: 'auto', whiteSpace: 'nowrap' }}>
                                                    {category.last_updated ? new Date(category.last_updated).toLocaleString() : 'Never'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ p: 1 }}>
                                                {Object.entries(fields).map(([fKey, field]) => (
                                                    <DataFieldRow key={fKey} label={field.label} value={field.value} format={field.format} naReason={field.na_reason} />
                                                ))}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            };

                            return (
                                <>
                                    {/* Fed Macro Liquidity Section */}
                                    {fedMacroEntries.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography sx={{
                                                fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                color: '#1e293b', mb: 1.2,
                                                borderLeft: '3px solid #059669', pl: 1,
                                            }}>
                                                Fed Macro Liquidity
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {fedMacroEntries.map(renderCategoryCard)}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Options & Volatility Desk Section */}
                                    {optionsDeskEntries.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography sx={{
                                                fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                color: '#1e293b', mb: 1.2,
                                                borderLeft: '3px solid #8b5cf6', pl: 1,
                                            }}>
                                                Options & Volatility Desk
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {optionsDeskEntries.map(renderCategoryCard)}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* The Crypto / Divergence Strategist Section */}
                                    {cryptoDivergenceEntries.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography sx={{
                                                fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                color: '#1e293b', mb: 1.2,
                                                borderLeft: '3px solid #f59e0b', pl: 1,
                                            }}>
                                                The Crypto / Divergence Strategist
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {cryptoDivergenceEntries.map(renderCategoryCard)}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* VWAP Execution Trader Section */}
                                    {vwapExecutionEntries.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography sx={{
                                                fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                color: '#1e293b', mb: 1.2,
                                                borderLeft: '3px solid #0ea5e9', pl: 1,
                                            }}>
                                                VWAP Execution Trader
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {vwapExecutionEntries.map(renderCategoryCard)}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Other Data Category Cards */}
                                    <Grid container spacing={2}>
                                        {otherEntries.map(renderCategoryCard)}
                                    </Grid>
                                </>
                            );
                        })()}

                        {/* Recent Candles: Numbers + Chart side-by-side
                            (1-hour hybrid window: user-TF for ~55 min + 30s bars for last 5 min) */}
                        {(marketData.recent_candles
                            || marketData.recent_candles_data?.length > 0
                            || marketData.recent_candles_intra_30s?.length > 0) && (
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                {/* Left: Candle Numbers */}
                                {marketData.recent_candles && marketData.recent_candles !== 'No candle data available' && (
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1, color: '#1e293b' }}>
                                                Recent Candles
                                                {marketData.agent_timezone && (
                                                    <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                                        (Timezone: {marketData.agent_timezone})
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Box sx={{
                                                fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap',
                                                color: '#475569', bgcolor: '#f8fafc', p: 1.5, borderRadius: '8px',
                                                maxHeight: 540, overflowY: 'auto',
                                                '&::-webkit-scrollbar': { width: 6 },
                                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
                                            }}>
                                                {marketData.recent_candles}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}
                                {/* Right: Stacked charts — user-TF on top, 30s tail below */}
                                {(marketData.recent_candles_data?.length > 0
                                    || marketData.recent_candles_intra_30s?.length > 0) && (
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                            {marketData.recent_candles_data?.length > 0 && (
                                                <>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        Chart ({selectedAgent?.symbol})
                                                        {/* Live/Stale dot — flips green when SSE events are flowing */}
                                                        <Box component="span" sx={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            backgroundColor: streamLive ? '#22c55e' : '#94a3b8',
                                                            boxShadow: streamLive ? '0 0 6px rgba(34,197,94,0.6)' : 'none',
                                                        }} />
                                                        <Typography component="span" sx={{ fontFamily, fontWeight: 500, fontSize: '0.7rem', color: streamLive ? '#16a34a' : '#94a3b8' }}>
                                                            {streamLive ? 'Live' : 'Stale'}
                                                        </Typography>
                                                        {marketData.agent_timezone && (
                                                            <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 'auto' }}>
                                                                (Timezone: {marketData.agent_timezone})
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <CandleChart ref={chartTopRef} data={marketData.recent_candles_data} height={260} />
                                                </>
                                            )}
                                            {marketData.recent_candles_intra_30s?.length > 0 && (
                                                <Box sx={{ mt: marketData.recent_candles_data?.length > 0 ? 2 : 0, pt: marketData.recent_candles_data?.length > 0 ? 2 : 0, borderTop: marketData.recent_candles_data?.length > 0 ? '1px dashed #e2e8f0' : 'none' }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', mb: 1, color: '#1e293b' }}>
                                                        Last 5 Minutes (30s)
                                                        <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                                            ({marketData.recent_candles_intra_30s.length} bars)
                                                        </Typography>
                                                    </Typography>
                                                    <CandleChart ref={chartBottomRef} data={marketData.recent_candles_intra_30s} height={160} secondsVisible />
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </>
                )}
            </Box>
        );
    };

    // ==================== AGENT DIALOG ====================

    const renderAgentDialog = () => (
        <Dialog open={agentDialog} onClose={() => setAgentDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>
                {agentEditing ? 'Edit AI Agent' : 'Create AI Agent'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Name" size="small" value={agentForm.name}
                            onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Symbol</InputLabel>
                            <Select value={agentForm.symbol_id} label="Symbol"
                                onChange={(e) => {
                                    const sym = watchlistSymbols.find(s => s.id === e.target.value);
                                    const mkt = String(sym?.market_type ?? sym?.asset_class ?? sym?.type ?? '').toUpperCase();
                                    const symText = String(sym?.symbol || '').toUpperCase();
                                    // Crypto tickers + Coinbase (per user classification). Other crypto-adjacent
                                    // equities (MSTR, miners) trade regular NASDAQ hours, so treat as stocks.
                                    const CRYPTO_PROXY_TICKERS = new Set(['COIN']);
                                    const looksCrypto = /(^|[^A-Z])(BTC|ETH|SOL|XRP|DOGE|ADA|BNB|LTC|AVAX|MATIC|DOT)([^A-Z]|$)/.test(symText)
                                        || symText.endsWith('-USD') || symText.endsWith('USDT') || symText.endsWith('USDC')
                                        || CRYPTO_PROXY_TICKERS.has(symText);
                                    const isKnownEquity = mkt === 'NYSE' || mkt === 'NASDAQ' || mkt === 'CBOE' || mkt === 'STOCK' || mkt === 'EQUITY';
                                    const isKnown247 = mkt === 'CRYPTO' || mkt === 'FOREX';
                                    // Unclassified (OTHER / blank) falls back to the ticker heuristic —
                                    // crypto-looking tickers go 24/7, everything else defaults to US equity hours.
                                    const is247 = isKnown247 || (!isKnownEquity && looksCrypto);
                                    const isUsEquity = isKnownEquity || (!isKnown247 && !looksCrypto);
                                    setAgentForm({
                                        ...agentForm,
                                        symbol_id: e.target.value,
                                        symbol: sym?.symbol || '',
                                        trading_start_time: is247 ? '' : (isUsEquity ? '08:30' : agentForm.trading_start_time),
                                        trading_end_time: is247 ? '' : (isUsEquity ? '15:00' : agentForm.trading_end_time),
                                        trading_timezone: isUsEquity ? 'America/Chicago' : (agentForm.trading_timezone || 'America/Chicago'),
                                    });
                                }}
                                sx={{ fontFamily }}>
                                {symbolsLoading ? (
                                    <MenuItem disabled sx={{ fontFamily }}>Loading symbols...</MenuItem>
                                ) : watchlistSymbols.map((s) => (
                                    <MenuItem key={s.id} value={s.id} sx={{ fontFamily }}>
                                        {s.symbol} {s.name ? `- ${s.name}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            px: 1.5, py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            bgcolor: agentEditing ? '#f8fafc' : '#fff',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                    Execution Mode
                                </Typography>
                                {agentEditing && (
                                    <Tooltip title="Execution mode is locked once an agent is created.">
                                        <MuiIcons.Lock sx={{ fontSize: 16, color: '#94a3b8' }} />
                                    </Tooltip>
                                )}
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                                    {agentForm.execution_mode === 'LLM'
                                        ? 'Trades via the active prompt in Prompt Editor.'
                                        : 'Trades via the Pure Code Execution & Risk Router.'}
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={agentForm.execution_mode}
                                disabled={!!agentEditing}
                                onChange={(_, v) => { if (v) setAgentForm({ ...agentForm, execution_mode: v }); }}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        fontFamily, textTransform: 'none', px: 2, py: 0.5,
                                        fontSize: '0.78rem', fontWeight: 600,
                                    },
                                    '& .MuiToggleButton-root.Mui-selected': {
                                        bgcolor: ACCENT, color: '#fff',
                                        '&:hover': { bgcolor: '#d97706' },
                                    },
                                }}
                            >
                                <ToggleButton value="NO_LLM">No LLM</ToggleButton>
                                <ToggleButton value="LLM">LLM</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Broker Connection (for auto-execute)</InputLabel>
                            <Select value={agentForm.broker_connection_id || ''} label="Broker Connection (for auto-execute)"
                                onChange={(e) => setAgentForm({ ...agentForm, broker_connection_id: e.target.value })}
                                sx={{ fontFamily }}>
                                <MenuItem value="" sx={{ fontFamily }}>
                                    <em>None (decisions logged only, no orders sent)</em>
                                </MenuItem>
                                {brokerConnectionsLoading ? (
                                    <MenuItem disabled sx={{ fontFamily }}>Loading brokers...</MenuItem>
                                ) : brokerConnections.length === 0 ? (
                                    <MenuItem disabled sx={{ fontFamily }}>No broker connections — create one in Order Management</MenuItem>
                                ) : brokerConnections.map((bc) => (
                                    <MenuItem key={bc.id} value={bc.id} sx={{ fontFamily }}>
                                        {bc.broker_name} {bc.connection_name ? `- ${bc.connection_name}` : ''} {bc.is_paper_trading ? '(Paper)' : '(Live)'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" size="small" multiline rows={2} value={agentForm.description}
                            onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                                Temperature: {agentForm.temperature}
                            </Typography>
                            <Slider value={agentForm.temperature} min={0} max={1} step={0.1}
                                onChange={(_, v) => setAgentForm({ ...agentForm, temperature: v })}
                                sx={{ color: ACCENT }} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Capital Allocation ($)" size="small" type="number" value={agentForm.capital_allocation}
                            onChange={(e) => setAgentForm({ ...agentForm, capital_allocation: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Position Value ($)" size="small" type="number" value={agentForm.max_position_value}
                            onChange={(e) => setAgentForm({ ...agentForm, max_position_value: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Daily Loss ($)" size="small" type="number" value={agentForm.max_daily_loss}
                            onChange={(e) => setAgentForm({ ...agentForm, max_daily_loss: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" disabled={!!agentEditing}>
                            <InputLabel>Timeframe</InputLabel>
                            <Select
                                label="Timeframe"
                                value={agentForm.timeframe}
                                onChange={(e) => setAgentForm({ ...agentForm, timeframe: e.target.value })}
                                sx={{ fontFamily }}
                            >
                                {Object.entries(TimeFrame).map(([key, val]) => (
                                    <MenuItem key={val} value={val} sx={{ fontFamily }}>
                                        {TimeFrameLabels[key]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Decision Timing (seconds)"
                            size="small"
                            type="number"
                            value={agentForm.cycle_interval_seconds}
                            onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setAgentForm({
                                    ...agentForm,
                                    cycle_interval_seconds: Number.isFinite(n) && n > 0 ? n : '',
                                });
                            }}
                            inputProps={{ min: 1 }}
                            helperText="LLM cycle / Live Market Data refresh cadence — default 30s"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Daily Cycles" size="small" type="number" value={agentForm.max_daily_cycles}
                            onChange={(e) => setAgentForm({ ...agentForm, max_daily_cycles: parseInt(e.target.value) || 50 })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Open Positions" size="small" type="number" value={agentForm.max_open_positions}
                            onChange={(e) => setAgentForm({ ...agentForm, max_open_positions: parseInt(e.target.value) || 3 })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading Start Time" size="small" type="time" value={agentForm.trading_start_time}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_start_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank for 24/7 (crypto)"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading End Time" size="small" type="time" value={agentForm.trading_end_time}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_end_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank for 24/7 (crypto)"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Trading Timezone</InputLabel>
                            <Select value={agentForm.trading_timezone} label="Trading Timezone"
                                onChange={(e) => setAgentForm({ ...agentForm, trading_timezone: e.target.value })}
                                sx={{ fontFamily }}>
                                {timezones.map(tz => (
                                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', mb: 0.25 }}>
                                Strategist Agents
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mb: 1 }}>
                                This agent will trade using only the selected strategists. Uncheck any strategist to exclude it from this configuration.
                            </Typography>
                            <FormGroup row>
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.macro_strategist}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, macro_strategist: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#059669' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Macro Strategist Agent</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.options_volatility}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, options_volatility: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#8b5cf6' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Options Volatility Agent</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.asset_strategist}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, asset_strategist: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#f59e0b' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Asset Strategist Agent</Typography>}
                                />
                            </FormGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={agentForm.auto_execute} onChange={(e) => setAgentForm({ ...agentForm, auto_execute: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Auto-execute orders (creates orders from AI decisions automatically)</Typography>}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setAgentDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleCreateAgent} disabled={submitting || !agentForm.name}
                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                    {agentEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>You do not have permission to view AI Trading Agents.</Alert>
            </Box>
        );
    }

    // ==================== TAB 6: P&L REPORT ====================

    const renderPnlReport = () => {
        const summary = pnlData?.summary || {};
        const trades = pnlData?.trades || [];
        const pnlColor = (val) => val > 0 ? '#16a34a' : val < 0 ? '#dc2626' : '#64748b';

        return (
            <Box>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.AccountBalance} title="Total P&L" value={`$${(summary.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.total_pnl)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.TrendingUp} title="Win Rate" value={`${summary.win_rate || 0}%`} color={(summary.win_rate || 0) >= 50 ? '#16a34a' : '#dc2626'} subtitle={`${summary.winning_trades || 0}W / ${summary.losing_trades || 0}L`} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.SwapVert} title="Total Trades" value={summary.total_trades || 0} color="#6366f1" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.ArrowUpward} title="Best Trade" value={`$${(summary.best_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#16a34a" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.ArrowDownward} title="Worst Trade" value={`$${(summary.worst_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#dc2626" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.Functions} title="Avg P&L / Trade" value={`$${(summary.avg_pnl_per_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.avg_pnl_per_trade)} subtitle={`PF: ${summary.profit_factor || 0}`} />
                    </Grid>
                </Grid>

                {/* Trades Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    {pnlLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} /></Box>
                    ) : trades.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <MuiIcons.ReceiptLong sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No closed trades yet for this agent</Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell sx={headCellSx}>Exit Date</TableCell>
                                            <TableCell sx={headCellSx}>Symbol</TableCell>
                                            <TableCell sx={headCellSx}>Side</TableCell>
                                            <TableCell sx={headCellSx} align="right">Entry</TableCell>
                                            <TableCell sx={headCellSx} align="right">Exit</TableCell>
                                            <TableCell sx={headCellSx} align="right">Shares</TableCell>
                                            <TableCell sx={headCellSx} align="right">P&L ($)</TableCell>
                                            <TableCell sx={headCellSx} align="right">P&L (%)</TableCell>
                                            <TableCell sx={headCellSx} align="right">Duration</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trades.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell sx={bodyCellSx}>{t.exit_date ? new Date(t.exit_date).toLocaleString() : '—'}</TableCell>
                                                <TableCell sx={bodyCellSx}><strong>{t.symbol}</strong></TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip label={t.side} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: t.side === 'LONG' ? '#dcfce7' : '#fee2e2', color: t.side === 'LONG' ? '#16a34a' : '#dc2626' }} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">${t.entry_price?.toFixed(2)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">${t.exit_price?.toFixed(2)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{t.quantity}</TableCell>
                                                <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: pnlColor(t.pnl) }} align="right">
                                                    {t.pnl > 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, color: pnlColor(t.pnl_pct) }} align="right">
                                                    {t.pnl_pct > 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">
                                                    {t.duration_minutes ? `${Math.floor(t.duration_minutes / 60)}h ${t.duration_minutes % 60}m` : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div" count={pnlTotal} page={pnlPage} rowsPerPage={pnlPerPage}
                                onPageChange={(_, p) => setPnlPage(p)}
                                onRowsPerPageChange={(e) => { setPnlPerPage(parseInt(e.target.value, 10)); setPnlPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                            />
                        </>
                    )}
                </Paper>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.5rem', color: '#1e293b' }}>
                    AI Trading Agents
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                    AI-powered quantitative trading agents using Gemini LLM for automated analysis and execution
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Selected Agent Indicator */}
            {selectedAgent && activeTab >= 2 && (
                <Alert severity="info" sx={{ mb: 2, fontFamily, '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1 } }}
                    action={<Button size="small" onClick={() => { setSelectedAgent(null); setActiveTab(0); }} sx={{ fontFamily, textTransform: 'none' }}>Change</Button>}>
                    Viewing: <strong>{selectedAgent.name}</strong> ({selectedAgent.symbol})
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 500, minHeight: 48 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT },
                    }}>
                    <Tab value={0} label="Agent Management" icon={<MuiIcons.SmartToy sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab value={1} label="Connect Account" icon={<MuiIcons.Cable sx={{ fontSize: 18 }} />} iconPosition="start" />
                    {selectedAgent && selectedAgent.execution_mode !== 'NO_LLM' && <Tab value={2} label="Prompt Editor" icon={<MuiIcons.Code sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={3} label="Decision Log" icon={<MuiIcons.ListAlt sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={4} label="Performance" icon={<MuiIcons.BarChart sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={5} label="Live Market Data" icon={<MuiIcons.DataUsage sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={6} label="P&L Report" icon={<MuiIcons.Assessment sx={{ fontSize: 18 }} />} iconPosition="start" />}
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderAgentManagement()}
            {activeTab === 1 && <ConnectAccountTab canAdd={canAdd} canEdit={canEdit} canDelete={canDelete} onConnectionsChange={fetchBrokerConnections} />}
            {selectedAgent && activeTab === 2 && renderPromptEditor()}
            {selectedAgent && activeTab === 3 && renderDecisionLog()}
            {selectedAgent && activeTab === 4 && renderPerformance()}
            {selectedAgent && activeTab === 5 && renderLiveMarketData()}
            {selectedAgent && activeTab === 6 && renderPnlReport()}

            {/* Dialogs */}
            {renderAgentDialog()}

            {/* Snackbar */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                {snackbar && <Alert severity={snackbar.severity} sx={{ fontFamily }}>{snackbar.message}</Alert>}
            </Snackbar>
        </Box>
    );
};

export default AIAgentPage;
